import { Outlet, NavLink, Link, useNavigate } from "react-router-dom";
import { Home, Calendar, Users, Image as ImageIcon, ShoppingBag, Instagram, Facebook, Youtube, Menu, X, User, LogOut, LayoutDashboard, Shield } from "lucide-react";
import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import NotificationBell from "@/components/NotificationBell";

const navItems = [
  { to: "/", label: "Home", end: true },
  { to: "/events", label: "Events" },
  { to: "/squares", label: "Board" },
  { to: "/shop", label: "Shop" },
  { to: "/gallery", label: "Gallery" },
  { to: "/about", label: "About" },
];

const bottomNav = [
  { to: "/", label: "Home", icon: Home, end: true },
  { to: "/events", label: "Events", icon: Calendar },
  { to: "/shop", label: "Shop", icon: ShoppingBag },
  { to: "/gallery", label: "Gallery", icon: ImageIcon },
  { to: "/dashboard", label: "Portal", icon: User },
];

// TikTok inline icon (not in lucide)
const TikTok = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M16.6 5.82A4.28 4.28 0 0 1 15.54 3h-3.09v12.4a2.59 2.59 0 1 1-2.59-2.59c.27 0 .53.04.77.12V9.4a5.99 5.99 0 0 0-.77-.05A5.84 5.84 0 1 0 15.7 15.2V8.9a7.3 7.3 0 0 0 4.3 1.38V7.19a4.28 4.28 0 0 1-3.4-1.37z"/></svg>
);

export default function Layout() {
  const [open, setOpen] = useState(false);
  const [social, setSocial] = useState({});
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { api.get("/content").then((r) => setSocial(r.data)).catch(() => {}); }, []);

  const handleLogout = () => { logout(); navigate("/"); };
  const socials = [
    { url: social.instagram_url || "https://instagram.com/i65_ryders", icon: Instagram, key: "ig" },
    { url: social.facebook_url || "https://facebook.com/i65ryders", icon: Facebook, key: "fb" },
    { url: social.tiktok_url || "https://tiktok.com/@i65_ryders", icon: TikTok, key: "tt" },
    { url: social.youtube_url || "https://youtube.com/@i65_ryders", icon: Youtube, key: "yt" },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-[#0c0b0a]">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#0c0b0a]/85 border-b border-[#d4af37]/15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 sm:h-20 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="header-logo-link">
            <Logo className="h-11 sm:h-14" />
          </Link>

          <nav className="hidden md:flex items-center gap-6 lg:gap-8 mx-auto">
            {navItems.map((n) => (
              <NavLink key={n.label} to={n.to} end={n.end}
                className={({ isActive }) => `font-display font-700 uppercase tracking-wider text-[15px] transition-colors ${isActive ? "text-[#f3c649]" : "text-[#d6cec2] hover:text-[#f3c649]"}`}
                data-testid={`nav-${n.label.toLowerCase()}`}>
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <div className="hidden md:flex items-center gap-2 text-[#d6cec2]">
              {socials.slice(0, 2).map((s) => { const I = s.icon; return (
                <a key={s.key} href={s.url} target="_blank" rel="noreferrer" className="hover:text-[#f3c649] transition-colors" data-testid={`header-${s.key}`}><I size={18} /></a>
              ); })}
            </div>
            <Link to="/join" className="hidden sm:inline-flex items-center rounded-full gold-border text-[#f3c649] px-4 lg:px-5 py-2 text-sm font-display uppercase tracking-wider hover:bg-[#f3c649] hover:text-black transition-colors" data-testid="header-join">
              Join The Ryders
            </Link>
            {user && <NotificationBell />}
            {user ? (
              <div className="hidden md:flex items-center gap-2">
                <Link to={user.role === "admin" ? "/admin" : "/dashboard"} className="text-[#d6cec2] hover:text-[#f3c649]" data-testid="header-portal">
                  {user.role === "admin" ? <Shield size={20} /> : <LayoutDashboard size={20} />}
                </Link>
                <button onClick={handleLogout} className="text-[#8f857a] hover:text-[#e0481f]" data-testid="header-logout"><LogOut size={18} /></button>
              </div>
            ) : (
              <Link to="/login" className="hidden md:inline-flex text-[#d6cec2] hover:text-[#f3c649]" data-testid="header-login"><User size={20} /></Link>
            )}
            <button className="md:hidden text-[#f7f3ec]" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
              {open ? <X size={26} /> : <Menu size={26} />}
            </button>
          </div>
        </div>

        {open && (
          <div className="md:hidden border-t border-[#d4af37]/15 bg-[#141210] px-4 py-4 space-y-1">
            {navItems.map((n) => (
              <Link key={n.label} to={n.to} onClick={() => setOpen(false)} className="block py-2.5 font-display uppercase tracking-wider text-lg text-[#f7f3ec]" data-testid={`mobile-nav-${n.label.toLowerCase()}`}>{n.label}</Link>
            ))}
            <div className="pt-3 flex items-center gap-4">
              {socials.map((s) => { const I = s.icon; return <a key={s.key} href={s.url} target="_blank" rel="noreferrer" className="text-[#d6cec2]"><I size={20} /></a>; })}
            </div>
            <div className="pt-3 flex flex-col gap-2">
              <Link to="/join" onClick={() => setOpen(false)} className="btn-gold rounded-full px-4 py-2.5 text-center font-display uppercase tracking-wide">Join The Ryders</Link>
              {user ? (
                <>
                  <Link to={user.role === "admin" ? "/admin" : "/dashboard"} onClick={() => setOpen(false)} className="btn-outline-cream rounded-full px-4 py-2.5 text-center font-display uppercase tracking-wide">{user.role === "admin" ? "Admin" : "My Portal"}</Link>
                  <button onClick={() => { setOpen(false); handleLogout(); }} className="text-[#8f857a] py-2 font-display uppercase tracking-wide">Log Out</button>
                </>
              ) : (
                <Link to="/login" onClick={() => setOpen(false)} className="btn-outline-cream rounded-full px-4 py-2.5 text-center font-display uppercase tracking-wide">Member Login</Link>
              )}
            </div>
          </div>
        )}
      </header>

      <main className="flex-1 pb-20 md:pb-0">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="border-t border-[#d4af37]/15 bg-[#0a0908] px-6 py-8 pb-24 md:pb-8">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6">
          <Logo className="h-14" />
          <p className="headline text-lg sm:text-xl text-[#d6cec2] tracking-[0.2em] text-center">DIFFERENT TRAILS. SAME FAMILY.</p>
          <div className="flex flex-col items-center gap-3">
            <Link to="/portal" className="inline-flex items-center gap-2 rounded-full gold-border text-[#f3c649] px-4 py-1.5 text-xs font-display uppercase tracking-wider hover:bg-[#f3c649] hover:text-black transition-colors" data-testid="footer-member-login">
              <User size={15} /> Member Login
            </Link>
            <div className="flex items-center gap-4 text-[#d6cec2]">
              {socials.map((s) => { const I = s.icon; return (
                <a key={s.key} href={s.url} target="_blank" rel="noreferrer" className="hover:text-[#f3c649] transition-colors" data-testid={`footer-${s.key}`}><I size={20} /></a>
              ); })}
            </div>
          </div>
        </div>
      </footer>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-50 bg-[#141210]/95 backdrop-blur-xl border-t border-[#d4af37]/20">
        <div className="grid grid-cols-5">
          {bottomNav.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink key={n.label} to={n.to} end={n.end}
                className={({ isActive }) => `flex flex-col items-center justify-center py-2.5 gap-1 ${isActive ? "text-[#f3c649]" : "text-[#8f857a]"}`}
                data-testid={`bottomnav-${n.label.toLowerCase()}`}>
                <Icon size={22} />
                <span className="text-[10px] font-display uppercase tracking-wider">{n.label}</span>
              </NavLink>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
